import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppDescription from '@nxsys/components/AppDescription';
import AppLoader from '@nxsys/components/AppLoader';
import AppIconCurrency from '@nxsys/components/AppIconCurrency';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  medicalInsuranceTypes,
  prsiEeClasses,
  uscExemptionOptions,
} from '@nxsys/constants/psr';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { revenueActions } from '@nxsys/store/slices/revenueFilings/reducer/revenueSlice';
import { Payroll } from '@nxsys/store/slices/revenueFilings/types';
import { Icons } from '@nxsys/types/icons';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { calcInsurable } from '@nxsys/utils/psr';
import { Card, Col, Descriptions, Row, Space, Typography } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';

const { Title, Text } = Typography;

type SubmissionDetailModalProps = {
  isOpen: boolean;
  toggle: () => void;
  data: Payroll[] | null;
  refetchData: () => void;
};

const SubmissionDetailModal: React.FC<SubmissionDetailModalProps> = ({
  isOpen,
  toggle,
  data,
  refetchData,
}: SubmissionDetailModalProps) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [payrollList, setPayrollList] = useState<Payroll[]>([]);

  useEffect(() => {
    const getPayrollList = async () => {
      if (!data || data.length === 0) return;
      setLoading(true);
      const res = await Promise.all(
        data.map((item) =>
          dispatch(revenueActions.getPayrollById({ id: item.id })).unwrap(),
        ),
      );
      setPayrollList(res.map((item) => item.data));
      setLoading(false);
    };
    if (data && isOpen) getPayrollList();
    if (!isOpen) setPayrollList([]);
  }, [data, isOpen]);

  const company = payrollList?.[0]?.subcontractorRef?.companyPayment;
  const submitId = !isNaN(payrollList[0]?.psrSubmissionLatest?.submitId)
    ? payrollList[0]?.psrSubmissionLatest?.submitId
    : 0;
  const submissionId = `submission-${payrollList[0]?.id}_${submitId + 1}`;
  const payrollRunReference = `payroll-run-reference-${payrollList[0]?.id}_${submitId + 1}`;

  const listPaymentDate = payrollList?.map((e) => moment(e?.paymentDate)) || [];
  const minDate = moment.min(listPaymentDate).format('DD/MM/YYYY');
  const maxDate = moment.max(listPaymentDate).format('DD/MM/YYYY');

  const totalTax = payrollList?.reduce(
    (temp, obj) => temp + obj?.irelandValue?.tax || 0,
    0,
  );
  const totalEePRSI = payrollList?.reduce(
    (temp, obj) => temp + obj?.irelandValue?.eePRSI || 0,
    0,
  );
  const totalErPRSI = payrollList?.reduce(
    (temp, obj) => temp + obj?.irelandValue?.erPRSI || 0,
    0,
  );
  const totalUSC = payrollList?.reduce(
    (temp, obj) => temp + obj?.irelandValue?.usc || 0,
    0,
  );
  const totalLPT = payrollList?.reduce(
    (temp, obj) => temp + obj?.irelandValue?.lptDeducted || 0,
    0,
  );
  const totalPension = payrollList?.reduce(
    (temp, obj) =>
      temp + obj?.irelandValue?.eePension + obj?.irelandValue?.erPension,
    0,
  );

  const headerSection = [
    {
      title: messages['psr.submit.submissionDetail'],
      icon: (
        <Icons.IconFileText color={defaultTheme.theme.palette.primary[600]} />
      ),
      items: [
        {
          label: messages['psr.submit.employerName'],
          value: company?.companyName ?? '',
        },
        {
          label: messages['psr.submit.employerRegistrationNumber'],
          value: company?.employerRegistrationNumber ?? '',
        },
        {
          label: messages['psr.submit.payPeriodStartDate'],
          value: minDate ?? '',
        },
        {
          label: messages['psr.submit.payPeriodEndDate'],
          value: maxDate ?? '',
        },
        {
          label: messages['psr.submit.submissionId'],
          value: submissionId ?? '',
        },
        {
          label: messages['psr.submit.payrollRunReference'],
          value: payrollRunReference ?? '',
        },
        {
          label: messages['psr.submit.softwareVersion'],
          value: '1.0',
        },
      ],
    },
    {
      title: messages['psr.submit.totals'],
      icon: <AppIconCurrency />,
      items: [
        {
          label: messages['psr.submit.numberOfEmployee'],
          value: data?.length ?? 0,
        },
        {
          label: messages['psr.submit.totalTax'],
          value: formatMoney(totalTax, showCurrencyByCountry(IRELAND)),
        },
        {
          label: messages['psr.submit.totalEePRSI'],
          value: formatMoney(totalEePRSI, showCurrencyByCountry(IRELAND)),
        },
        {
          label: messages['psr.submit.totalErPRSI'],
          value: formatMoney(totalErPRSI, showCurrencyByCountry(IRELAND)),
        },
        {
          label: messages['psr.submit.totalUSC'],
          value: formatMoney(totalUSC, showCurrencyByCountry(IRELAND)),
        },
        {
          label: messages['psr.submit.totalLPT'],
          value: formatMoney(totalLPT, showCurrencyByCountry(IRELAND)),
        },
        {
          label: messages['psr.submit.totalPension'],
          value: formatMoney(totalPension, showCurrencyByCountry(IRELAND)),
        },
      ],
    },
  ];

  const getInformationByPayroll = (payroll: Payroll) => [
    {
      title: messages['psr.submit.employmentDetails'],
      icon: <Icons.IconUser color={defaultTheme.theme.palette.primary[600]} />,
      items: [
        {
          label: messages['psr.submit.uniqueReference'],
          value: payroll.subcontractorRef?.agencyRef ?? '',
        },
        {
          label: messages['psr.submit.addressLine1'],
          value: payroll.subcontractorRef?.address[0] ?? '',
        },
        {
          label: messages['psr.submit.addressLine2'],
          value: payroll.subcontractorRef?.address[1] ?? '',
        },
        {
          label: messages['psr.submit.county'],
          value: payroll.subcontractorRef?.county ?? '',
        },
        {
          label: messages['psr.submit.country'],
          value: payroll.subcontractorRef?.country ?? '',
        },
        {
          label: messages['psr.submit.dateOfBirth'],
          value:
            moment(payroll.subcontractorRef?.DOB).format('DD/MM/YYYY') ?? '',
        },
        {
          label: messages['psr.submit.payFrequency'],
          value: payroll.subcontractorRef?.payFrequency ?? '',
        },
      ],
    },
    {
      title: messages['psr.submit.paymentDetails'],
      icon: <AppIconCurrency />,
      items: [
        {
          label: messages['psr.submit.lineItemId'],
          value: `${payroll?.id}_${submissionId + 1}`,
        },
        {
          label: messages['psr.submit.calculationBasis'],
          value: payroll?.taxBasis ?? '',
        },
        {
          label: messages['psr.submit.payDate'],
          value: moment(payroll.paymentDate).format('DD/MM/YYYY') ?? '',
        },
        {
          label: messages['psr.submit.grossPay'],
          // Mirrors the payload's data item 24: taxGross already carries every
          // component of pay, and taxDeduction is exactly the reliefs taken off
          // it, so adding them back gives pay before reliefs. This read
          // costToER — the employer's total cost, gross plus employer PRSI —
          // which is not what is filed and is always higher.
          value: formatMoney(
            (payroll.irelandValue?.taxGross || 0) +
              Math.max(0, payroll.irelandValue?.taxDeduction || 0),
            showCurrencyByCountry(IRELAND),
          ),
        },
      ],
    },
    {
      title: messages['psr.submit.prsi'],
      icon: (
        <Icons.IconBuilding color={defaultTheme.theme.palette.primary[600]} />
      ),
      items: [
        {
          label: messages['psr.submit.employerPrsiAblePay'],
          value: formatMoney(
            payroll.irelandValue?.erPrsiGross,
            showCurrencyByCountry(IRELAND),
          ),
        },
        {
          label: messages['psr.submit.employerPrsiPaid'],
          value: formatMoney(
            payroll.irelandValue?.erPRSI,
            showCurrencyByCountry(IRELAND),
          ),
        },
        {
          label: messages['psr.submit.employeePrsiAblePay'],
          value: formatMoney(
            payroll.irelandValue?.eePrsiGross,
            showCurrencyByCountry(IRELAND),
          ),
        },
        {
          label: messages['psr.submit.employeePrsiPaid'],
          value: formatMoney(
            payroll.irelandValue?.eePRSI,
            showCurrencyByCountry(IRELAND),
          ),
        },
        {
          label: messages['psr.submit.prsiClass'],
          value:
            prsiEeClasses?.find(
              (c) =>
                c?.value === payroll?.subcontractorRef?.setting?.prsiEeClasses,
            )?.label ?? '',
        },
        {
          label: messages['psr.submit.insurableWeeks'],
          value: calcInsurable(
            payroll?.subcontractorRef?.setting?.payFrequency,
          ),
        },
      ],
    },
    {
      title: messages['psr.submit.usc'],
      icon: (
        <Icons.IconBuilding color={defaultTheme.theme.palette.primary[600]} />
      ),
      items: [
        {
          label: messages['psr.submit.uscStatus'],
          value:
            uscExemptionOptions?.find(
              (s) =>
                s?.value === payroll?.subcontractorRef?.setting?.uscExemption,
            )?.label ?? '',
        },
        {
          label: messages['psr.submit.uscAblePay'],
          value: formatMoney(
            payroll.irelandValue?.uscGross,
            showCurrencyByCountry(IRELAND),
          ),
        },
        {
          label: messages['psr.submit.uscPaid'],
          value: formatMoney(
            payroll.irelandValue?.usc,
            showCurrencyByCountry(IRELAND),
          ),
        },
      ],
    },
    {
      title: messages['psr.submit.other'],
      icon: (
        <Icons.IconFileText color={defaultTheme.theme.palette.primary[600]} />
      ),
      items: [
        {
          label: messages['psr.submit.medicalInsurance'],
          value: formatMoney(
            payroll?.BIK?.reduce((temp, obj) => {
              if (
                obj?.type === medicalInsuranceTypes[0]?.value ||
                obj?.type === medicalInsuranceTypes[1]?.value
              ) {
                return temp + obj?.value || 0;
              }
            }, 0),
            showCurrencyByCountry(IRELAND),
          ),
        },
        {
          label: messages['psr.submit.taxableBenefits'],
          // Notional pay less the share-based part, as the payload sends it.
          // This read takeHomePay — the employee's net pay — so a worker with
          // no benefits at all was shown their whole net wage as a taxable
          // benefit.
          value: formatMoney(
            (payroll.irelandValue?.notionalPay || 0) -
              (payroll.irelandValue?.shareBasedREmuneration || 0),
            showCurrencyByCountry(IRELAND),
          ),
        },
      ],
    },
  ];

  const handleSubmit = async () => {
    setLoading(true);
    await dispatch(
      revenueActions.submitPSRByPayrollIds({
        ids: payrollList.map((item) => item.id),
      }),
    );
    setLoading(false);
    toggle();
    refetchData();
  };

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className="whitespace-nowrap">
            {messages['psr.submit.submissionDetail']}
          </div>
          <div className="flex gap-x-[8px]">
            <AppButton
              name={messages['common.cancel']}
              variant="outlined"
              onClick={toggle}
              disabled={loading}
            />
            <AppButton
              name={messages['psr.submit.sendToRevenue']}
              type="primary"
              onClick={handleSubmit}
              loading={loading}
            />
          </div>
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      width={'80vw'}
      hiddenFooter
      titleFullWidth
    >
      {loading && <AppLoader />}
      {payrollList.length !== 0 && (
        <div
          style={{
            padding: '0 8px',
            maxHeight: 'calc(100vh - 240px)',
            overflowY: 'auto',
          }}
        >
          {/* Header Summary Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
            {headerSection.map((section, index) => (
              <Col key={index} span={12}>
                <AppCard title={section.title} className="h-full">
                  <div className="p-[16px]">
                    <AppDescription
                      column={1}
                      labelStyle={{
                        width: 200,
                        fontWeight: 500,
                        color: theme.palette.gray[600],
                      }}
                      contentStyle={{
                        fontWeight: 400,
                        color: theme.palette.text.primary,
                      }}
                      bordered={false}
                      colon={false}
                      size="small"
                    >
                      {section?.items?.map((item, _index) => (
                        <Descriptions.Item key={_index} label={item.label}>
                          <Text strong>{item.value ?? '-'}</Text>
                        </Descriptions.Item>
                      ))}
                    </AppDescription>
                  </div>
                </AppCard>
              </Col>
            ))}
          </Row>

          {/* Employee Details */}
          <div style={{ marginTop: 24 }}>
            <Space align="center" className="mb-[24px]">
              <Icons.IconUserGroup
                color={defaultTheme.theme.palette.primary[600]}
              />
              <Title
                level={4}
                style={{ color: theme.palette.primary[600] }}
                className="mb-0"
              >
                Employee Details
              </Title>
            </Space>

            {payrollList?.map((payroll, payrollIndex) => (
              <Card
                key={payroll.id}
                style={{
                  marginBottom: 24,
                  borderRadius: theme.cardRadius,
                  boxShadow: theme.cardShadow,
                  border: `1px solid ${theme.palette.borderColor}`,
                }}
              >
                {/* Employee Header */}
                <div
                  style={{
                    marginBottom: 24,
                    padding: '16px 20px',
                    backgroundColor: theme.palette.primary[600],
                    borderRadius: theme.sizes.borderRadius.base,
                    color: 'white',
                  }}
                >
                  <div>
                    <Text strong style={{ color: 'white', fontSize: 18 }}>
                      {payroll?.subcontractorName || 'Employee Name'}
                    </Text>
                    <br />
                    <Text
                      style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}
                    >
                      Employee #{payrollIndex + 1}
                    </Text>
                  </div>
                </div>

                {/* Employee Information Sections */}
                <Row gutter={[24, 24]}>
                  {getInformationByPayroll(payroll).map((info, index) => (
                    <Col key={index} span={12}>
                      <Card
                        size="small"
                        style={{
                          borderRadius: theme.sizes.borderRadius.base,
                          border: `1px solid ${theme.divider}`,
                          background: theme.palette.gray[50],
                          height: '100%',
                        }}
                        title={
                          <Space align="center">
                            <span style={{ color: theme.palette.blue[500] }}>
                              {info.icon}
                            </span>
                            <Text
                              strong
                              style={{
                                fontSize: 14,
                                color: theme.palette.text.primary,
                              }}
                              className="mb-0"
                            >
                              {info.title}
                            </Text>
                          </Space>
                        }
                      >
                        <AppDescription
                          column={1}
                          labelStyle={{
                            width: 180,
                            fontWeight: 500,
                            color: theme.palette.gray[600],
                          }}
                          bordered={false}
                          colon={false}
                          size="small"
                        >
                          {info?.items?.map((item, _index) => (
                            <Descriptions.Item key={_index} label={item.label}>
                              <Text>{item.value ?? '-'}</Text>
                            </Descriptions.Item>
                          ))}
                        </AppDescription>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card>
            ))}
          </div>
        </div>
      )}
    </AppModalContainer>
  );
};

export default SubmissionDetailModal;
